// SP3-full slice 2 (D4) — the asset-canvas streaming live-fill consumer.
//
// The boat canvas's useFactoryPacketFill is react-hook-form + provenance-map
// coupled; the asset canvas holds a flat Record<string,string> form state, so
// this is its small sibling: subscribe to the SAME per-item copilot channel
// (`tenant.{tenantId}.copilot.{inventory_item_id}` — the ai-engine streamer
// publishes on inventory_item_id for ANY item type), map each {type:'field'}
// event onto the asset form keys, and hand the value to the caller. The
// 4-state provenance tints are slice 3; this slice reuses the SP3-lite
// AI-filled tint via the onField callback.
//
// Manual-edit-wins: the caller owns the user-edited set (typing into a field
// registers it); a streamed re-read never clobbers an edited field.
//
// #1179 (D52) — a photo read's field events (source_kind 'photo') are ignored:
// the merge keeps a photo value as a suggestion, never as a column value.
//
// Terminals (boat parity): `done`/`done_with_errors` fire onDone (clears the
// hero's "now reading…" state) + ONE reconcile invalidate of the asset detail
// query (dropped-event backstop); `failed` fires onFailed + toasts the
// user-safe message — a dead job never leaves the hero hung silently.

import { useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useChannel } from "@/lib/realtime/useChannel";
import { engineKeys } from "@/modules/inventory/hooks/useEngines";
import { trailerKeys } from "@/modules/inventory/hooks/useTrailers";
import { useVocabulary } from "@/modules/core/hooks/useVocabulary";
import type { ProvenanceSource } from "@/modules/inventory/types/boat.types";
import {
  resolveToCanonical,
  ENUM_FORM_FIELDS,
  type VocabularyIndex,
} from "@/modules/core/utils/vocabulary";
import type { AssetType } from "../asset-form/asset-form-config";
import { toAssetProvenanceSource } from "./assetProvenance";

/** ai-engine engineCore stream key → asset form key. */
const ENGINE_STREAM_TO_FORM: Record<string, string> = {
  manufacturer: "engine_manufacturer",
  model: "engine_model",
  horsepower: "engine_horsepower",
  serial_number: "engine_serial_number",
  fuel_type: "fuel_type",
};

/** ai-engine trailerCore stream key → asset form key. */
const TRAILER_STREAM_TO_FORM: Record<string, string> = {
  vin: "trailer_vin",
  make: "trailer_make",
  axles: "trailer_axles",
  gvwr_lbs: "trailer_gvwr",
};

/**
 * Map one streamed field event onto an asset form key (the slice-2 D5 field
 * map). Only the FIRST entity of the canvas's own type (`engine#0` /
 * `trailer#0`) fills the form — a packet describing several engines must not
 * ping-pong the fields. `boat#0.condition` is the one cross-entity fill
 * (new|used only). Returns null when the event has no form slot.
 */
export function mapAssetStreamField(
  type: AssetType,
  entityRef: string,
  path: string,
  value: unknown,
  index: VocabularyIndex,
): { key: string; value: string } | null {
  if (value == null || value === "") return null;

  if (entityRef === "boat#0" && path === "condition") {
    const v = String(value).toLowerCase();
    return v === "new" || v === "used" ? { key: "condition", value: v } : null;
  }

  const map = type === "engine" ? ENGINE_STREAM_TO_FORM : TRAILER_STREAM_TO_FORM;
  if (entityRef !== `${type}#0`) return null;

  const key = map[path];
  if (!key) return null;

  const str = String(value);
  const final = (ENUM_FORM_FIELDS as readonly string[]).includes(key)
    ? resolveToCanonical(index, key, str)
    : str;
  return { key, value: final };
}

/** Provenance carried by each streamed field (slice 3 — drives the 4-state tint). */
export interface AssetStreamFieldMeta {
  source: ProvenanceSource;
  confidence: number;
}

export interface AssetExtractFillHandlers {
  /** Manual-edit-wins: true = the dealer touched this field, skip the stream write. */
  isUserEdited: (key: string) => boolean;
  /** A mapped field arrived — write the value + provenance entry. */
  onField: (key: string, value: string, meta: AssetStreamFieldMeta) => void;
  /** Terminal done / done_with_errors — clear the "now reading…" state. */
  onDone?: () => void;
  /** Terminal failed — clear the reading state (the toast fires here). */
  onFailed?: () => void;
}

export function useAssetExtractFill(
  type: AssetType,
  tenantId: string | null | undefined,
  itemId: string | null | undefined,
  handlers: AssetExtractFillHandlers,
): void {
  const queryClient = useQueryClient();
  const { index } = useVocabulary();
  const vocabIndexRef = useRef(index);
  vocabIndexRef.current = index;
  // Keep handlers in a ref so the channel subscription never re-binds on the
  // caller's fresh inline callbacks (the useFactoryPacketFill idiom).
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const channel: string | null =
    tenantId && itemId ? `tenant.${tenantId}.copilot.${itemId}` : null;

  useChannel<Record<string, unknown>>(channel, {
    onPublication: (env) => {
      // ai-engine publishes raw at ctx.data; Laravel broadcasts wrap in
      // {event, payload}. Unwrap defensively (mirror useFactoryPacketFill).
      const raw = env as unknown as Record<string, unknown>;
      const payload: Record<string, unknown> =
        raw && typeof raw === "object" && "type" in raw
          ? raw
          : ((raw as { payload?: Record<string, unknown> })?.payload ?? {});

      const eventType = (payload as { type?: string }).type;

      if (eventType === "field") {
        const f = payload as {
          entity_ref?: string;
          path?: string;
          value?: unknown;
          confidence?: unknown;
          source?: { doc_type?: string; source_type?: string } | null;
          source_kind?: string;
        };
        if (typeof f.entity_ref !== "string" || typeof f.path !== "string") return;
        // #1179 (D52) — the photos are read after the documents, and the merge never
        // writes a photo value to a column. This form is autosaved, so a photo value
        // written here would reach the column through the save. What the run merged
        // arrives through the re-read the run terminal triggers (below).
        if (f.source_kind === "photo") return;
        const mapped = mapAssetStreamField(type, f.entity_ref, f.path, f.value, vocabIndexRef.current);
        if (!mapped) return;
        if (handlersRef.current.isUserEdited(mapped.key)) return; // manual-edit-wins
        handlersRef.current.onField(mapped.key, mapped.value, {
          source: toAssetProvenanceSource(f.source),
          confidence: typeof f.confidence === "number" ? f.confidence : 0,
        });
        return;
      }

      if (eventType === "failed") {
        handlersRef.current.onFailed?.();
        const message =
          typeof (payload as { user_message?: unknown }).user_message === "string" &&
          ((payload as { user_message: string }).user_message ?? "").trim()
            ? (payload as { user_message: string }).user_message
            : "Document reading failed — please fill the fields manually or try again.";
        toast.error(message);
        return;
      }

      if (eventType === "done" || eventType === "done_with_errors") {
        handlersRef.current.onDone?.();
        // Reconcile backstop: a dropped WS event is recovered by one detail
        // refetch (the canvas's resume hydration is userEdited-guarded).
        if (itemId) {
          const keys = type === "engine" ? engineKeys : trailerKeys;
          queryClient.invalidateQueries({ queryKey: keys.detail(itemId) });
        }
      }
    },
  });
}
